// Editor.js block tool for the events module's listing block. Stores only
// display settings ({ filter, show_as, limit }) — the events themselves are
// fetched at runtime by the published site from the tenant's R2 events.json,
// so the editor shows a settings bar with a static explanation, not a preview.

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';

const FILTERS = [
  { value: "none", label: "All events" },
  { value: "featured", label: "Featured only" },
  { value: "not_featured", label: "Not featured" },
];

const LAYOUTS = [
  { value: "list", label: "List" },
  { value: "grid", label: "Grid" },
  // One-line announcement band showing only the next matching event.
  { value: "strip", label: "Strip" },
];

export class EventsTool {
  static get toolbox() {
    return { title: "Events", icon: ICON };
  }

  constructor({ data, block }) {
    this.block = block;
    this.data = {
      filter: FILTERS.some((f) => f.value === data?.filter) ? data.filter : "none",
      show_as: LAYOUTS.some((l) => l.value === data?.show_as) ? data.show_as : "list",
      limit: Number.isInteger(data?.limit) && data.limit > 0 ? Math.min(data.limit, 50) : null,
    };
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-gallery-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-gallery-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-gallery-tool__title";
    title.textContent = "Events";

    const filterSelect = document.createElement("select");
    filterSelect.className = "pgs-gallery-tool__select";
    filterSelect.setAttribute("aria-label", "Which events to show");
    for (const { value, label } of FILTERS) {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      filterSelect.appendChild(option);
    }
    filterSelect.value = this.data.filter;
    filterSelect.addEventListener("change", () => {
      this.data.filter = filterSelect.value;
      this.block?.dispatchChange?.();
    });

    const toggle = document.createElement("div");
    toggle.className = "pgs-gallery-tool__toggle";
    const buttons = LAYOUTS.map(({ value, label }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "pgs-gallery-tool__btn";
      button.textContent = label;
      button.addEventListener("click", () => {
        this.data.show_as = value;
        applyLayout();
        this.block?.dispatchChange?.();
      });
      toggle.appendChild(button);
      return { value, button };
    });
    const applyLayout = () => {
      for (const { value, button } of buttons) {
        button.classList.toggle("pgs-gallery-tool__btn--active", this.data.show_as === value);
      }
    };
    applyLayout();

    const limitInput = document.createElement("input");
    limitInput.type = "number";
    limitInput.min = "1";
    limitInput.max = "50";
    limitInput.placeholder = "All";
    limitInput.className = "pgs-gallery-tool__select";
    limitInput.style.width = "5rem";
    limitInput.setAttribute("aria-label", "Maximum events to show");
    if (this.data.limit) limitInput.value = String(this.data.limit);
    limitInput.addEventListener("change", () => {
      const parsed = parseInt(limitInput.value, 10);
      this.data.limit = parsed > 0 ? Math.min(parsed, 50) : null;
      this.block?.dispatchChange?.();
    });

    bar.append(title, filterSelect, toggle, limitInput);

    const note = document.createElement("div");
    note.className = "pgs-gallery-tool__preview";
    note.textContent =
      "Upcoming events from your Events page show here — they update on the live site as soon as you save an event, without publishing.";

    root.append(bar, note);
    return root;
  }

  save() {
    return { filter: this.data.filter, show_as: this.data.show_as, limit: this.data.limit };
  }
}
